import React, { useState, useRef, useEffect } from 'react';
import './DropdownMenu.css'; // 引入样式文件
import {Link} from 'react-router-dom';
import { PRODUCT_ITEMS, APP_URL } from '@/consts/Links.ts';
// 自定义 hook: 检测点击是否在元素外部
const useOutsideClick = (ref, callback) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      // 如果点击发生在目标元素外部
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };

    // 添加事件监听
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    // 清除事件监听
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [ref, callback]);
};

// 下拉菜单组件
export const DropdownMenu = ({ title, items }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // 使用自定义 hook 实现点击外部关闭菜单
  useOutsideClick(dropdownRef, () => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  // 鼠标悬停和离开的处理函数
  const handleMouseEnter = () => {
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  const handleItemClick = (item) => {
    window.scrollTo(0, 0)
    setIsOpen(false); // 点击后关闭菜单
  };

  return (
    // 使用 onMouseEnter/onMouseLeave 实现悬停打开/关闭
    <div
      className="dropdown-container"
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* 菜单标题 */}
      <div className={`dropdown-trigger ${isOpen ? 'active' : ''}`}>
        {title}
        <span className="arrow">
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
            <g opacity="0.6">
              <path fillRule="evenodd" clipRule="evenodd"
                d="M8.87629 4.10036C9.02974 4.24475 9.04213 4.49179 8.90396 4.65215L5.34018 8.79054C5.32013 8.83062 5.29302 8.86771 5.25898 8.89974C5.19035 8.96431 5.10513 8.99742 5.01953 8.99994C5.01296 8.9995 5.00629 8.99984 4.99961 9C4.99318 8.99981 4.98677 8.99948 4.98038 8.99897C4.89487 8.99742 4.80965 8.96431 4.74102 8.89974C4.70698 8.86771 4.67987 8.83062 4.65982 8.79054L1.09604 4.65215C0.957868 4.49179 0.970258 4.24475 1.12371 4.10036C1.27716 3.95597 1.51357 3.96892 1.65173 4.12928L5.00005 8.01504L8.34826 4.12928C8.48643 3.96892 8.72284 3.95597 8.87629 4.10036Z"
                fill="white" />
            </g>
          </svg>
        </span>
      </div>

      {/* 下拉内容，根据 isOpen 状态显示/隐藏 */}
      {isOpen && (
        <div className="dropdown-menu">
          {items.map((item, index) => {
            const isExternal = item.url?.startsWith('http')
            // External product links must be real anchors (react-router Link mangles absolute URLs).
            return isExternal ? (
              <a
                href={item.url}
                key={index}
                target={item?.isBlank ? '_blank' : '_self'}
                rel="noopener noreferrer"
                className="dropdown-item"
                onClick={() => handleItemClick(item)}
              >
                {item.label}
              </a>
            ) : (
              <Link
                to={item.url}
                key={index}
                target={item?.isBlank ? '_blank' : '_self'}
                className="dropdown-item"
                onClick={() => handleItemClick(item)}
              >
                {item.label}
              </Link>
            )
          })}
        </div>
      )}
    </div>
  );
};

// 示例用法
const Navbar = () => {
  const handleBlogClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // const solutions = [
  //   // {
  //   //   label: 'Voyager',
  //   //   url: '/solutions?key=voyager'
  //   // }, {
  //   //   label: 'Apollo',
  //   //   url: '/solutions?key=apollo'
  //   // },
  //   {
  //   label: 'Non-Custodial Utility',
  //   url: '/nonCustodial'
  // }, {
  //   label: 'Compliant Custodial Utility',
  //   url: '/custodial'
  // }];

  const useCases = [{
    label: 'Institutional Digital Assets',
    url: '/digitalAssets'
  }, {
    label: 'Onchain Private Credit',
    url: '/privateCredit'
  }, {
    label: 'Tokenized RWA',
    url: '/tokenizedRWA'
  }];

  const resources = [
    {
      label: 'Blogs',
      url: '/blog'
    },
    // {
    //   label: 'Doc',
    //   isBlank: true,
    //   url: 'https://docs.vishwanetwork.xyz/'
    // },
    // {
    //   label: 'Podcast',
    //   url: '/podcast'
    // },
  ];

  return (
    <nav className="navbar">
      <DropdownMenu title="Products" items={PRODUCT_ITEMS} />
      <Link to='/blog' className="nav-link" onClick={handleBlogClick}>Blogs</Link>

      {/* 右上角的按钮 */}
      {/* Primary CTA → the agent-curated, Veta-guarded vault dapp */}
      <div className={`get-touch-btn ml-[48px]`}>
        <Link target="_blank" to={APP_URL}>
          <div className="get-touch-btn-text text-center">Launch App</div>
        </Link>
        <div className={`get-touch-mask`} />
      </div>
      <div className={`get-touch-btn ml-[16px]`}>
        <Link target="_blank" to="https://defillama.com/protocol/vishwa">
          <div className="get-touch-btn-text text-center">Vishwa Stats</div>
        </Link>
        <div className={`get-touch-mask`} />
      </div>

    </nav>
  );
};

export default Navbar;